import React, { useState, useEffect } from 'react';
import { useUserFeedback } from '../../hooks/useUserFeedback';
import { Button } from '../ui/button';
import { Loader2, Save, AlertCircle, CheckCircle } from 'lucide-react';

interface AppFeedbackFormData {
  ratingFriends?: number;
  ratingCustomers?: number;
  otherComments?: string;
}

const initialAppFeedbackData: AppFeedbackFormData = {
  ratingFriends: 0,
  ratingCustomers: 0,
  otherComments: ''
};

const RatingInput: React.FC<{
  label: string;
  value: number;
  onChange: (value: number) => void;
  max?: number;
}> = ({ label, value, onChange, max = 10 }) => (
  <div className="space-y-3">
    <label className="block text-sm font-medium text-rm-ink-900">{label}</label>
    <div className="inline-block">
      <div className="flex space-x-1.5 flex-wrap">
        {[...Array(max)].map((_, i) => (
          <button
            key={i}
            type="button"
            onClick={() => onChange(i + 1)}
            className={`w-8 h-8 rounded-full border-2 text-xs font-medium transition-all duration-200 hover:scale-110 focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-rm-accent-500 ${
              value > i 
                ? 'bg-rm-accent-600 border-rm-accent-600 text-white shadow-soft' 
                : 'border-rm-neutral-600/30 text-rm-neutral-600 hover:border-rm-accent-500 hover:text-rm-accent-600 bg-white'
            }`}
          >
            {i + 1}
          </button>
        ))}
      </div>
      <div className="flex justify-between text-xs text-rm-neutral-600 mt-2">
        <span className="text-left">1 – Will never recommend</span>
        <span className="text-right">10 – Will recommend</span>
      </div>
    </div>
  </div>
);

export const AppFeedbackSection: React.FC = () => {
  const {
    appFeedbackLoading,
    appFeedbackError,
    submitAppFeedback,
    clearAppFeedback,
    clearErrors
  } = useUserFeedback();

  // Local form state for app feedback
  const [appData, setAppData] = useState<AppFeedbackFormData>(initialAppFeedbackData);
  const [appSuccess, setAppSuccess] = useState(false);

  // Only clear errors on mount - don't load stored feedback
  useEffect(() => {
    clearErrors();
  }, [clearErrors]);

  // Don't update form data when app feedback loads - keep form empty
  // This effect has been removed to maintain empty form behavior

  // Handle app form submission
  const handleAppSubmit = async () => {
    setAppSuccess(false);
    try {
      const success = await submitAppFeedback(appData);
      if (success) {
        // Clear form and reset to initial state after successful submission
        setAppData(initialAppFeedbackData);
        clearAppFeedback();
        setAppSuccess(true);
        setTimeout(() => setAppSuccess(false), 3000);
      }
    } catch (error) {
      console.error('App feedback submission error:', error);
    }
  };

  const handleInputChange = (field: keyof AppFeedbackFormData, value: string | number) => {
    setAppData({ ...appData, [field]: value });
  };

  // Check if data has changed from original (always true for empty form)
  const hasChanged = JSON.stringify(appData) !== JSON.stringify(initialAppFeedbackData);
  
  // Validation: Both ratings are required, other comments is optional
  const isValid = (appData.ratingFriends || 0) > 0 && (appData.ratingCustomers || 0) > 0;

  return (
    <div className="bg-white shadow-card rounded-card p-6 border border-rm-neutral-600/20">
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-rm-primary-700">App Feedback</h2>
      </div>
      
      <div className="space-y-6">
        <RatingInput
          label="How likely are you to recommend Kim’s service to a colleague?"
          value={appData.ratingFriends || 0}
          onChange={(value) => handleInputChange('ratingFriends', value)}
          max={10}
        />
        
        <RatingInput
          label="How likely are you to recommend Kim’s service to a corporate customer?"
          value={appData.ratingCustomers || 0}
          onChange={(value) => handleInputChange('ratingCustomers', value)}
          max={10}
        />

        <div className="space-y-3">
          <label className="block text-sm font-medium text-rm-ink-900">
            Please, leave your advice to Kim here to become a better Buddy for you
          </label>
          <textarea
            value={appData.otherComments || ''}
            onChange={(e) => handleInputChange('otherComments', e.target.value)}
            rows={4}
            className="w-full px-3 py-2 border border-rm-neutral-600/30 rounded-card shadow-soft focus:outline-none focus:ring-2 focus:ring-rm-accent-500 focus:border-rm-accent-500 resize-vertical text-rm-ink-900"
            placeholder="Any additional feedback or suggestions..."
          />
        </div>
        
        {appFeedbackError && (
          <div className="flex items-center space-x-2 text-red-600 text-sm bg-red-50 rounded-card p-3">
            <AlertCircle className="w-4 h-4 flex-shrink-0" />
            <span>{appFeedbackError}</span>
          </div>
        )}
        
        {appSuccess && (
          <div className="flex items-center space-x-2 text-green-600 text-sm bg-green-50 rounded-card p-3">
            <CheckCircle className="w-4 h-4 flex-shrink-0" />
            <span>App feedback was sent successfully!</span>
          </div>
        )}
        
        <div className="text-xs text-rm-neutral-600 bg-rm-accent-300/30 rounded-card p-3 border border-rm-accent-300/50">
          We track your feedback over time to better understand how your experience evolves.
        </div>
        
        <div className="pt-2">
          <Button
            onClick={handleAppSubmit}
            disabled={appFeedbackLoading || !isValid || !hasChanged}
            className="w-full"
          >
            {appFeedbackLoading ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Saving...
              </>
            ) : (
              <>
                <Save className="w-4 h-4 mr-2" />
                Send App Feedback
              </>
            )}
          </Button>
        </div>
      </div>
    </div>
  );
};
